import { type CSSProperties, Fragment, useId, useMemo } from 'react';

import clsx from 'clsx';
import { Bar as ReBar, BarChart as ReBarChart, CartesianGrid, Line, Tooltip as ReTooltip } from 'recharts';

import { CustomBar } from '@/components/common/charts/BarChart/components/CustomBar/CustomBar';
import { useXAxis, type XLabelFormatter } from '@/components/common/charts/hooks/use-x-axis';
import { useYAxis, type YLabelFormatter } from '@/components/common/charts/hooks/use-y-axis';
import { Legend } from '@/components/common/charts/Legend';
import { ResponsiveContainer } from '@/components/common/charts/ResponsiveContainer';
import { Tooltip } from '@/components/common/charts/Tooltip';
import { type DateXAxisType, type XAxisType, type XValue } from '@/components/common/charts/types';
import { Flex } from '@/components/common/Flex';
import { type WithClassName } from '@/types/react';
import { type RegularColor } from '@/types/theme';
import { getColorCssValue } from '@/utils/theme';

import styles from './BarChart.module.scss';

export type Bar = {
  id: string;
  name: string;
  values: number[];
  color: RegularColor;
  categoryId?: string;
};

type XAxisProps<XType extends XAxisType> = {
  xAxisType: XType;
};

type BarChartProps<XType extends XAxisType> = WithClassName & {
  bars: Bar[];
  xValues: XValue<XType>[];
  yTicks?: number[];
  withLegend?: boolean;
  formatXLabel?: XLabelFormatter<XType>;
  yAxisWidth?: number;
  formatYLabel?: YLabelFormatter;
} & (XType extends DateXAxisType ? Partial<XAxisProps<XType>> : XAxisProps<XType>);

const DEFAULT_CATEGORY_ID = 'default';

export const BarChart = <XType extends XAxisType = 'datetime'>(props: BarChartProps<XType>) => {
  const {
    className,
    bars,
    xValues,
    yTicks,
    withLegend,
    xAxisType = 'datetime',
    formatXLabel,
    yAxisWidth,
    formatYLabel,
  } = props;

  const chartId = useId();

  const allZeroes = bars.every(({ values }) => values.every((value) => value === 0));

  const chartData = useMemo(() => {
    return xValues.map((x, valueIndex) => ({
      x: x.valueOf(),
      'bar-fake': yTicks?.at(-1) ?? 0,
      ...Object.fromEntries(bars.map(({ id, values }) => [`bar-${id}`, values.at(valueIndex) ?? null])),
    }));
  }, [bars, xValues, yTicks]);

  const yValues = useMemo(() => {
    return bars.map(({ values }) => values);
  }, [bars]);

  const fillColorIds = useMemo(() => {
    return bars.reduce<Partial<Record<string, string>>>((colorIds, line) => {
      colorIds[line.id] = `${chartId}-fillBarColor-${line.id}`;

      return colorIds;
    }, {});
  }, [bars, chartId]);

  const strokeColorIds = useMemo(() => {
    return bars.reduce<Partial<Record<string, string>>>((colorIds, line) => {
      colorIds[line.id] = `${chartId}-strokeBarColor-${line.id}`;

      return colorIds;
    }, {});
  }, [bars, chartId]);

  const colorsStyle = useMemo(() => {
    const style: CSSProperties = {};

    bars.forEach(({ id, color }) => {
      style[`--bar-${id}-color`] = getColorCssValue(color);
    });

    return style;
  }, [bars]);

  const xAxis = useXAxis<XType>({
    type: xAxisType as XType,
    rechartsType: 'category',
    values: xValues,
    formatter: formatXLabel,
  });

  const yAxis = useYAxis({
    values: yValues,
    ticks: yTicks,
    formatter: formatYLabel,
    width: yAxisWidth,
  });

  return (
    <Flex className={clsx(styles.root, className)} direction="column" gap={9}>
      <ResponsiveContainer>
        <ReBarChart data={chartData}>
          <defs>
            {bars.map(({ id, color }) => (
              <Fragment key={id}>
                <linearGradient id={fillColorIds[id]} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={getColorCssValue(color)} stopOpacity={0.26} />
                  <stop offset="100%" stopColor={getColorCssValue(color)} stopOpacity={0} />
                </linearGradient>

                <linearGradient id={strokeColorIds[id]} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={getColorCssValue(color)} stopOpacity={1} />
                  <stop offset="100%" stopColor={getColorCssValue(color)} stopOpacity={0} />
                </linearGradient>
              </Fragment>
            ))}
          </defs>

          {xAxis}
          {yAxis}

          <CartesianGrid horizontal={true} vertical={false} syncWithTicks stroke="var(--dark-green2-13p)" />

          {allZeroes && (
            <Line dataKey="bar-fake" stroke="transparent" legendType="none" dot={false} activeDot={false} />
          )}

          {bars.map(({ id, color, categoryId = DEFAULT_CATEGORY_ID }) => (
            <ReBar
              key={id}
              dataKey={`bar-${id}`}
              stackId={categoryId}
              fill={`url(#${fillColorIds[id]})`}
              stroke={`url(#${strokeColorIds[id]})`}
              strokeWidth={1}
              shape={CustomBar}
              style={{
                '--color': getColorCssValue(color),
              }}
            />
          ))}

          <ReTooltip
            shared={false}
            cursor={false}
            content={Tooltip}
            isAnimationActive={false}
            wrapperStyle={colorsStyle}
          />
        </ReBarChart>
      </ResponsiveContainer>

      {withLegend && <Legend datasets={bars} />}
    </Flex>
  );
};
